import type { ReactNode } from 'react';
import React from 'react';

import { QBox, QStack } from '@captivateiq/quotient';

export type ChatLayoutProps = {
  topContent?: ReactNode;
  children: ReactNode;
  bottomContent?: ReactNode;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  contentContainerProps?: React.ComponentProps<typeof QStack>;
};

export const TabLayout = ({
  topContent,
  children,
  bottomContent,
  onScroll,
  contentContainerProps,
}: ChatLayoutProps) => {
  return (
    <QStack
      direction="column"
      sx={{
        alignItems: 'stretch',
        flex: 1,
        height: '100%',
        paddingBottom: {
          xs: 'calc(env(safe-area-inset-bottom, 0px) + var(--mobile-bottom-nav-height, 56px))',
          md: 0,
        },
      }}
    >
      {topContent && (
        <QBox
          sx={{
            backgroundColor: 'white',
            borderBottom: '1px solid',
            borderBottomColor: 'grey.300',
            padding: '12px',
          }}
        >
          {topContent}
        </QBox>
      )}

      <QStack
        direction="column"
        sx={{
          alignItems: 'stretch',
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          // Legacy _global.scss sets a thick gray scrollbar that overrides the quotient theme's
          // intended thin/transparent one; restated per-component until that global rule is fixed.
          '&::-webkit-scrollbar': { background: 'transparent', width: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'neutral.primary.200', borderRadius: '4px' },
        }}
        onScroll={onScroll}
      >
        <QStack
          direction="column"
          spacing="10px"
          sx={{
            alignItems: 'stretch',
            backgroundColor: 'grey.50',
            flex: 1,
            justifyContent: 'flex-start',
            padding: '16px',
            ...contentContainerProps?.sx,
          }}
          {...contentContainerProps}
        >
          {children}
        </QStack>
      </QStack>

      {bottomContent && (
        <QBox
          sx={{
            backgroundColor: 'white',
            borderTop: '1px solid',
            borderTopColor: 'grey.300',
            padding: '16px 16px 24px',
            width: '100%',
          }}
        >
          {bottomContent}
        </QBox>
      )}
    </QStack>
  );
};
